import { Fragment, useState } from 'react';
import { Check, ChevronDown, FileText, Paperclip, Pencil, Plus } from 'lucide-react';
import type { TaskStatusCategory, TaskStatusDef } from '../../../lib/taskStatusesApi';
import { useTaskStatuses } from '../../../hooks/useTaskStatuses';
import { StatusIcon } from '../../tasks/linear/taskVisuals';
import { AttachmentImage } from '../../ui/AttachmentImage';
import { useI18n } from '../../../contexts/I18nContext';
import { useMyDailyBoard, useSetCheckIn, useUserDailyBoard } from '../../../hooks/useDailyStatus';
import type {
  CheckInStatus,
  CheckInView,
  TodayReason,
  TodayTask,
} from '../../../lib/dailyStatusApi';
import type { CommentAttachment } from '../../../lib/tasksApi';
import { TaskListRow, dailyTaskRow } from '../../tasks/TaskListRow';
import { CommentComposerBox } from '../../tasks/linear/CommentComposerBox';
import { Popover } from '../../tasks/linear/Popover';
import { DailyReportDrawer } from './DailyReportDrawer';

interface Props {
  userId: string;
  date: string;
  /** The viewer is looking at their own board: the controls are live. */
  isSelf: boolean;
  canExportPdf: boolean;
  canSendEmail: boolean;
}

/**
 * The quick statuses drawn exactly like the task list draws statuses: the
 * 14px StatusIcon keyed on the category, in the colour of the real status the
 * quick status maps to (so a custom "Done" colour shows here too). Blocat has
 * no task category — it is a red ring with a bar, in the same language.
 */
export const QUICK_STATUS_ORDER: CheckInStatus[] = [
  'DONE',
  'IN_PROGRESS',
  'BLOCKED',
  'NOT_STARTED',
];

const QUICK_CATEGORY: Record<Exclude<CheckInStatus, 'BLOCKED'>, TaskStatusCategory> = {
  DONE: 'COMPLETED',
  IN_PROGRESS: 'STARTED',
  NOT_STARTED: 'UNSTARTED',
};

const FALLBACK_COLOR: Record<CheckInStatus, string> = {
  DONE: '#22c55e',
  IN_PROGRESS: '#3b82f6',
  BLOCKED: '#ef4444',
  NOT_STARTED: '#9ca3af',
};

/** The colour of the first configured status in the mapped category, else a default. */
export function useQuickStatusColor(): (status: CheckInStatus) => string {
  const { listGroupOrder } = useTaskStatuses();
  return (status) => {
    if (status === 'BLOCKED') return FALLBACK_COLOR.BLOCKED;
    const category = QUICK_CATEGORY[status];
    const rows = listGroupOrder.filter((s) => s.category === category);
    // IN_PROGRESS maps to the earliest STARTED stage (In Progress, not In Review).
    const pick = status === 'IN_PROGRESS' ? rows[rows.length - 1] : rows[0];
    return pick?.color ?? FALLBACK_COLOR[status];
  };
}

function BlockedIcon({ color, size = 14 }: { color: string; size?: number }) {
  const c = size / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle cx={c} cy={c} r={c - 1.5} fill="none" stroke={color} strokeWidth={1.5} />
      <path
        d={`M${c * 0.55} ${c} L${c * 1.45} ${c}`}
        stroke={color}
        strokeWidth={1.6}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function QuickStatusIcon({
  status,
  color,
  size = 14,
}: {
  status: CheckInStatus;
  color: string;
  size?: number;
}) {
  if (status === 'BLOCKED') return <BlockedIcon color={color} size={size} />;
  return <StatusIcon category={QUICK_CATEGORY[status]} color={color} size={size} />;
}

const REASON_TONE: Record<TodayReason, string> = {
  due_today: 'text-blue-600 border-blue-300',
  overdue: 'text-red-600 border-red-300',
  started: 'text-blue-600 border-blue-300',
  worked_on: 'text-gray-500 border-gray-300',
  completed_today: 'text-emerald-600 border-emerald-300',
  checked_in: 'text-gray-500 border-gray-300',
};

/** The one reason worth a chip: the first in the board's priority order. */
function primaryReason(task: TodayTask): TodayReason | null {
  const order: TodayReason[] = ['overdue', 'due_today', 'started', 'completed_today', 'worked_on'];
  return order.find((r) => task.reasons.includes(r)) ?? null;
}

/** The quick state a configured status reports as — mirrors the server's mapping. */
function quickFor(category: TaskStatusCategory): CheckInStatus {
  if (category === 'COMPLETED') return 'DONE';
  if (category === 'STARTED') return 'IN_PROGRESS';
  return 'NOT_STARTED';
}

/** The status a note is saved under when no status was chosen yet. */
function impliedStatus(task: TodayTask): CheckInStatus {
  if (task.statusCategory === 'COMPLETED') return 'DONE';
  if (task.statusCategory === 'STARTED' || task.hoursToday > 0 || task.timerRunning) {
    return 'IN_PROGRESS';
  }
  return 'NOT_STARTED';
}

function isImage(a: CommentAttachment): boolean {
  return a.contentType.startsWith('image/');
}

function AttachmentChips({ attachments }: { attachments: CommentAttachment[] }) {
  if (attachments.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-2">
      {attachments.map((a, i) =>
        isImage(a) ? (
          // Lightbox, not a second window — same as every other picture in the app.
          <AttachmentImage
            key={`${a.name}-${i}`}
            src={a.url}
            alt={a.name}
            className="h-14 w-14 rounded-md border border-gray-200 object-cover"
            placeholderClassName="h-14 w-14"
          />
        ) : (
          <a
            key={`${a.name}-${i}`}
            href={a.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded border border-gray-200 px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-100"
          >
            <Paperclip size={10} /> {a.name}
          </a>
        ),
      )}
    </div>
  );
}

/**
 * The confirmed status, read-only (someone else's board, or a past day). Shows
 * the exact status name when the check-in carries one, and falls back to the
 * quick-state label for check-ins written before the picker offered them.
 */
function ConfirmedStatusBadge({ checkIn }: { checkIn: CheckInView }) {
  const { t } = useI18n();
  const colorOf = useQuickStatusColor();
  const { byId } = useTaskStatuses();
  const status = checkIn.taskStatusId ? byId[checkIn.taskStatusId] : undefined;
  const color = status?.color ?? colorOf(checkIn.status);
  return (
    <span
      className="inline-flex h-7 items-center gap-1.5 rounded-md border border-gray-300 px-2 text-[12px] font-medium"
      style={{ color }}
    >
      {status ? (
        <StatusIcon category={status.category} color={color} size={14} />
      ) : (
        <QuickStatusIcon status={checkIn.status} color={color} size={14} />
      )}
      {status?.name ?? t(`dailyStatus.status.${checkIn.status}`)}
    </span>
  );
}

/** What the picker hands back: one configured status, or the Blocat state. */
export type StatusPick = { kind: 'status'; status: TaskStatusDef } | { kind: 'blocked' };

function StatusDropdown({
  statusId,
  blocked,
  confirmed,
  disabled,
  onSelect,
  onClear,
}: {
  /** The status shown on the trigger — the confirmed one, else the task's current one. */
  statusId: string | null;
  /** The check-in says Blocat, which no task status can express. */
  blocked: boolean;
  /** A check-in exists for today: solid border. Otherwise dashed = not confirmed yet. */
  confirmed: boolean;
  disabled: boolean;
  onSelect: (pick: StatusPick) => void;
  onClear: () => void;
}) {
  const { t } = useI18n();
  const colorOf = useQuickStatusColor();
  const { listGroupOrder, byId } = useTaskStatuses();
  const current = statusId ? byId[statusId] : undefined;
  const blockedColor = colorOf('BLOCKED');
  const color = blocked ? blockedColor : current?.color;
  const label = blocked ? t('dailyStatus.status.BLOCKED') : (current?.name ?? null);
  return (
    <Popover
      width={210}
      align="end"
      trigger={({ open, toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          onClick={toggle}
          disabled={disabled}
          aria-haspopup="menu"
          aria-expanded={open}
          title={confirmed ? undefined : t('dailyStatus.pickStatus')}
          className={`inline-flex h-8 min-w-[44px] items-center gap-1.5 rounded-md border px-2 text-[12px] font-medium transition-colors disabled:opacity-60 sm:h-7 ${
            confirmed
              ? 'border-gray-300 hover:bg-gray-100'
              : 'border-dashed border-gray-400/60 hover:border-gray-400'
          }`}
          style={color ? { color } : undefined}
        >
          {blocked ? (
            <QuickStatusIcon status="BLOCKED" color={blockedColor} size={14} />
          ) : (
            <StatusIcon
              category={current?.category ?? 'UNSTARTED'}
              color={color ?? '#9ca3af'}
              size={14}
            />
          )}
          <span className="hidden sm:inline">{label ?? t('dailyStatus.pickStatus')}</span>
          <ChevronDown size={12} className="opacity-70" />
        </button>
      )}
    >
      {(close) => (
        <div className="py-1">
          {/* The real configured statuses, in the same order the task list
              groups them — so "În review" is pickable here like anywhere else. */}
          {listGroupOrder.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                onSelect({ kind: 'status', status: s });
                close();
              }}
              className="ui-menu-item justify-between"
            >
              <span className="flex items-center gap-2">
                <StatusIcon category={s.category} color={s.color} size={14} />
                <span style={{ color: s.color }}>{s.name}</span>
              </span>
              {!blocked && statusId === s.id && <Check size={14} className="text-gray-400" />}
            </button>
          ))}
          {/* Blocat is a check-in state, not a task status: it never moves the
              task, and it needs a reason before it saves. */}
          <div className="my-1 border-t border-gray-200" />
          <button
            type="button"
            onClick={() => {
              onSelect({ kind: 'blocked' });
              close();
            }}
            className="ui-menu-item justify-between"
          >
            <span className="flex items-center gap-2">
              <QuickStatusIcon status="BLOCKED" color={blockedColor} size={14} />
              <span style={{ color: blockedColor }}>{t('dailyStatus.status.BLOCKED')}</span>
            </span>
            {blocked && <Check size={14} className="text-gray-400" />}
          </button>
          {confirmed && (
            <>
              <div className="my-1 border-t border-gray-200" />
              <button
                type="button"
                onClick={() => {
                  onClear();
                  close();
                }}
                className="ui-menu-item text-gray-500"
              >
                <StatusIcon category="UNSTARTED" color="#9ca3af" size={14} />
                {t('dailyStatus.clearStatus')}
              </button>
            </>
          )}
        </div>
      )}
    </Popover>
  );
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

function StatusRow({ task, date, editable }: { task: TodayTask; date: string; editable: boolean }) {
  const { t } = useI18n();
  const mutation = useSetCheckIn(date);
  const [editing, setEditing] = useState(false);
  // "Blocat" picked without a reason yet: held until the note is written.
  const [pendingBlocked, setPendingBlocked] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>('idle');

  const checkIn = task.checkIn;
  const blocked = pendingBlocked || checkIn?.status === 'BLOCKED';
  // Prefilled: the confirmed status when there is a check-in, otherwise the
  // status the task actually sits on right now — so a status already changed
  // during the day is one click to confirm, not something to remember.
  const shownStatusId = blocked ? null : (checkIn?.taskStatusId ?? task.statusId);
  const hasNote = Boolean(checkIn?.comment || (checkIn?.attachments.length ?? 0) > 0);

  function save(
    status: CheckInStatus,
    taskStatusId: string | null,
    comment: string | null,
    attachments: CommentAttachment[],
  ) {
    setSaveState('saving');
    return new Promise<void>((resolve) => {
      mutation.mutate(
        { taskId: task.id, input: { status, taskStatusId, comment, attachments } },
        {
          onSuccess: () => {
            setPendingBlocked(false);
            setEditing(false);
            setSaveState('saved');
            resolve();
          },
          onError: () => {
            setSaveState('error');
            resolve();
          },
        },
      );
    });
  }

  function onPick(pick: StatusPick) {
    const comment = checkIn?.comment ?? null;
    const attachments = checkIn?.attachments ?? [];
    if (pick.kind === 'blocked') {
      if (!comment) {
        // The reason is required: open the note box and save once it is written.
        setPendingBlocked(true);
        setEditing(true);
        return;
      }
      setPendingBlocked(false);
      void save('BLOCKED', null, comment, attachments);
      return;
    }
    setPendingBlocked(false);
    // The quick state is derived server-side from the picked status; sending
    // the category's equivalent keeps the request valid on its own.
    void save(quickFor(pick.status.category), pick.status.id, comment, attachments);
  }

  function onClear() {
    setPendingBlocked(false);
    setEditing(false);
    setSaveState('saving');
    mutation.mutate(
      { taskId: task.id, input: null },
      { onSuccess: () => setSaveState('idle'), onError: () => setSaveState('error') },
    );
  }

  const reason = primaryReason(task);
  const tone =
    task.reasons.includes('overdue') && task.statusCategory !== 'COMPLETED' ? 'overdue' : 'plain';

  const trailing = (
    <div className="flex shrink-0 items-center gap-2 pr-2">
      {task.hoursToday > 0 && (
        <span className="hidden text-[11px] tabular-nums text-gray-400 md:inline">
          {t('dailyStatus.hours', { hours: task.hoursToday })}
        </span>
      )}
      {reason && (
        <span
          className={`hidden rounded-full border px-1.5 py-px text-[10px] font-medium uppercase tracking-wide lg:inline ${REASON_TONE[reason]}`}
        >
          {t(`dailyStatus.reason.${reason}`)}
        </span>
      )}
      {saveState === 'saving' && (
        <span className="hidden text-[11px] text-gray-400 sm:inline">
          {t('dailyStatus.saving')}
        </span>
      )}
      {saveState === 'error' && (
        <span className="text-[11px] text-red-600">{t('dailyStatus.saveError')}</span>
      )}
      {editable ? (
        <StatusDropdown
          statusId={shownStatusId}
          blocked={blocked}
          confirmed={checkIn !== null}
          disabled={mutation.isPending}
          onSelect={onPick}
          onClear={onClear}
        />
      ) : checkIn ? (
        <ConfirmedStatusBadge checkIn={checkIn} />
      ) : (
        <span className="text-[11px] text-amber-600">{t('dailyStatus.unreported')}</span>
      )}
    </div>
  );

  const showNoteArea = editing || hasNote || editable;
  const noteLabel = (
    <span className="mr-2 mt-1 hidden shrink-0 text-[11px] uppercase tracking-wide text-gray-400 sm:inline">
      {t('dailyStatus.note')}
    </span>
  );

  return (
    <Fragment>
      <TaskListRow task={dailyTaskRow(task)} tone={tone} newTab hideAssignee trailing={trailing} />
      {showNoteArea && (
        <li className="border-b border-gray-100 px-3 py-1.5 sm:pl-9">
          {editing ? (
            <div className="flex items-start">
              {noteLabel}
              <CommentComposerBox
                taskId={task.id}
                rows={4}
                autoFocus
                initialValue={checkIn?.comment ?? ''}
                initialAttachments={checkIn?.attachments ?? []}
                placeholder={
                  blocked
                    ? t('dailyStatus.blockedPlaceholder')
                    : t('dailyStatus.commentPlaceholder')
                }
                submitLabel={t('dailyStatus.saveComment')}
                busyLabel={t('dailyStatus.saving')}
                busy={mutation.isPending}
                keepAfterSubmit
                onCancel={() => {
                  setEditing(false);
                  setPendingBlocked(false);
                }}
                cancelLabel={t('dailyStatus.cancel')}
                onSubmit={(content, attachments) =>
                  blocked
                    ? save('BLOCKED', null, content || null, attachments)
                    : save(
                        checkIn?.status ?? impliedStatus(task),
                        shownStatusId,
                        content || null,
                        attachments,
                      )
                }
              />
            </div>
          ) : hasNote ? (
            <div className="flex items-start gap-2">
              {noteLabel}
              <div className="min-w-0 flex-1">
                {checkIn?.comment && (
                  <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-gray-700">
                    {checkIn.comment}
                  </p>
                )}
                <AttachmentChips attachments={checkIn?.attachments ?? []} />
              </div>
              {editable && (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  title={t('dailyStatus.editComment')}
                  aria-label={t('dailyStatus.editComment')}
                  className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                >
                  <Pencil size={13} />
                </button>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[12px] text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              <Plus size={12} /> {t('dailyStatus.addComment')}
            </button>
          )}
        </li>
      )}
    </Fragment>
  );
}

/**
 * "Statusul meu de azi" — the check-in board at the top of the Daily Summary.
 *
 * Every task the person should say something about today (due today,
 * overdue, in progress, worked on) with a status dropdown and the same
 * comment box as the task page (markdown, @mentions, photos and files). A
 * manager looking at someone else's day sees the same list read-only; past
 * days are read-only for everyone.
 */
export function MyDailyStatusSection({ userId, date, isSelf, canExportPdf, canSendEmail }: Props) {
  const { t } = useI18n();
  const mine = useMyDailyBoard(date, isSelf);
  const theirs = useUserDailyBoard(isSelf ? undefined : userId, date);
  const query = isSelf ? mine : theirs;
  const [reportOpen, setReportOpen] = useState(false);

  const board = query.data;
  const editable = Boolean(isSelf && board?.editable);

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="font-semibold text-gray-800">
            {isSelf
              ? t('dailyStatus.title')
              : t('dailyStatus.titleOther', { name: board?.user.name ?? '' })}
          </h2>
          {board && board.tasks.length > 0 && (
            <p className="mt-0.5 text-xs text-gray-500">
              {t('dailyStatus.progress', { done: board.checkedIn, total: board.tasks.length })}
              {!editable && isSelf && <> · {t('dailyStatus.readOnly')}</>}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {board && board.tasks.length > 0 && (
            <div className="hidden h-1.5 w-28 overflow-hidden rounded-full bg-gray-200 sm:block">
              <div
                className="h-full bg-emerald-500 transition-all"
                style={{ width: `${Math.round((board.checkedIn / board.tasks.length) * 100)}%` }}
              />
            </div>
          )}
          <button
            type="button"
            onClick={() => setReportOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-[13px] font-medium text-gray-700 hover:bg-gray-100"
          >
            <FileText size={14} />
            {t('dailyStatus.report')}
          </button>
        </div>
      </div>

      {query.isLoading && <p className="mt-3 text-sm text-gray-500">…</p>}
      {query.isError && <p className="mt-3 text-sm text-red-600">{t('dailyStatus.loadError')}</p>}
      {board && board.tasks.length === 0 && (
        <p className="mt-3 text-sm text-gray-500">{t('dailyStatus.empty')}</p>
      )}
      {board && board.tasks.length > 0 && (
        <ul className="mt-3 overflow-hidden rounded-md border border-gray-200">
          {board.tasks.map((task) => (
            <StatusRow key={task.id} task={task} date={date} editable={editable} />
          ))}
        </ul>
      )}

      <DailyReportDrawer
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        userId={isSelf ? undefined : userId}
        date={date}
        canExportPdf={canExportPdf}
        canSendEmail={canSendEmail}
      />
    </section>
  );
}
